import React, { useEffect, useState } from 'react';
import { ChevronDown, Target, Zap, Leaf } from 'lucide-react';
import { heroContent } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../utils/translations';
import { fetchPublicSettings, pickCmsShort } from '../services/publicContentService';

const Hero = ({ theme }) => {
  const [particles, setParticles] = useState([]);
  const { language } = useLanguage();
  const t = translations[language];
  const isAr = language === 'ar';

  // CMS-driven hero content — everything falls back to the static defaults.
  const [cms, setCms] = useState({});
  useEffect(() => {
    let alive = true;
    fetchPublicSettings('hero').then((v) => { if (alive) setCms(v || {}); });
    return () => { alive = false; };
  }, []);

  const heroTitle    = pickCmsShort(cms.title,    isAr) || t.heroTitle;
  const heroSubtitle = pickCmsShort(cms.subtitle, isAr) || t.heroSubtitle;
  const heroCta      = pickCmsShort(cms.cta_text, isAr) || t.learnMore;
  const bgLight = (cms.background_light && String(cms.background_light).trim()) || heroContent.backgroundImages.light[0];
  const bgDark  = (cms.background_dark  && String(cms.background_dark).trim())  || heroContent.backgroundImages.dark[0];

  const features = [
    { icon: Target, text: t.studyAccuracy, color: 'from-amber-500 to-orange-500' },
    { icon: Zap, text: t.fastResponse, color: 'from-blue-500 to-cyan-500' },
    { icon: Leaf, text: t.sustainableEnergy, color: 'from-green-500 to-emerald-500' }
  ];

  useEffect(() => {
    // Generate random particles (bubbles)
    const newParticles = Array.from({ length: 30 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 5,
      duration: 3 + Math.random() * 4,
      size: 4 + Math.random() * 8
    }));
    setParticles(newParticles);
  }, []);

  const scrollToAbout = () => {
    const aboutSection = document.getElementById('about');
    aboutSection?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative min-h-screen overflow-hidden flex items-center justify-center">
      {/* Background Ocean Image */}
      <div className="absolute inset-0">
        <div 
          className="absolute inset-0 bg-cover bg-center transition-opacity duration-1000"
          style={{
            backgroundImage: theme === 'light' 
              ? `url('${bgLight}')` 
              : `url('${bgDark}')`,
            backgroundPosition: 'center 30%'
          }}
        />
        
        {/* Overlay gradients for depth */}
        <div className="absolute inset-0 bg-gradient-to-b from-cyan-900/40 via-transparent to-slate-900/60 dark:from-slate-900/60 dark:via-transparent dark:to-black/80" />
        
        {/* Light rays effect */}
        {theme === 'light' && (
          <div className="absolute inset-0 opacity-30">
            <div className="light-ray" style={{ left: '20%' }} />
            <div className="light-ray" style={{ left: '50%', animationDelay: '1s' }} />
            <div className="light-ray" style={{ left: '70%', animationDelay: '2s' }} />
          </div>
        )}

        {/* Moon glow for dark mode */}
        {theme === 'dark' && (
          <div className="absolute top-20 right-1/4 w-32 h-32 bg-blue-200/10 rounded-full blur-3xl animate-pulse" />
        )}
      </div>

      {/* Floating Particles/Bubbles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {particles.map((particle) => (
          <div
            key={particle.id}
            className="absolute bottom-0 rounded-full bg-white/30 dark:bg-blue-200/20 animate-float-up"
            style={{
              left: `${particle.left}%`,
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              animationDelay: `${particle.delay}s`,
              animationDuration: `${particle.duration}s`
            }}
          />
        ))}
      </div>

      {/* Hero Content */}
      <div
  className={`relative z-10 container mx-auto px-4 text-center ${
    !isAr ? 'pt-20 sm:pt-16 lg:pt-0' : ''
  }`}
>
        <div className="max-w-4xl mx-auto space-y-8">
          {/* Main Title */}
          <h1 data-testid="hero-title" className="text-5xl md:text-7xl lg:text-8xl font-bold text-white drop-shadow-2xl animate-fade-in-up">
            {heroTitle}
          </h1>

          {/* Subtitle */}
          <p data-testid="hero-subtitle" className="text-lg md:text-xl lg:text-2xl text-cyan-50 dark:text-blue-100 drop-shadow-lg animate-fade-in-up animation-delay-200">
            {heroSubtitle}
          </p>

          {/* Feature Highlights */}
          <div className="flex flex-wrap justify-center gap-4 md:gap-6 pt-4 animate-fade-in-up animation-delay-300">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div 
                  key={index}
                 className="group relative backdrop-blur-md bg-white/10 dark:bg-slate-900/30 
         border border-white/20 dark:border-cyan-500/20
         rounded-2xl px-6 py-4 
         hover:bg-white/20 dark:hover:bg-slate-800/40
         transition-all duration-300 hover:scale-105
         hover:shadow-2xl hover:shadow-cyan-500/30"
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg bg-gradient-to-br ${feature.color} shadow-lg`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-sm md:text-base font-semibold text-white">
                      {feature.text}
                    </span>
                  </div>
                  {/* Glow effect on hover */}
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-cyan-400/0 via-cyan-400/10 to-cyan-400/0 
                                opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </div>
              );
            })}
          </div>

          {/* CTA Button */}
          <div className="pt-8 animate-fade-in-up animation-delay-400">
            <button
              onClick={scrollToAbout}
              className="group relative px-8 py-4 text-lg font-semibold text-white 
                       bg-gradient-to-r from-cyan-500 to-blue-600 
                       hover:from-cyan-400 hover:to-blue-500
                       rounded-full shadow-2xl 
                       transition-all duration-300 
                       hover:scale-105 hover:shadow-cyan-500/50
                       before:absolute before:inset-0 before:rounded-full 
                       before:bg-white/20 before:opacity-0 
                       hover:before:opacity-100 before:transition-opacity"
            >
              <span className="relative z-10 flex items-center gap-2">
                {heroCta}
                <ChevronDown className="w-5 h-5 animate-bounce" />
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <ChevronDown className="w-8 h-8 text-white/60" />
      </div>

      {/* Water Surface Effect (top of screen) */}
      <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-cyan-300/20 dark:from-blue-900/30 to-transparent pointer-events-none" />
    </section>
  );
};

export default Hero;
